import { DataLoader } from 'src/app/services/data-loader/data-loader';
import { DataLoaderResult } from 'src/app/services/data-loader/data-loader-result';
import { standardRun } from 'src/app/services/data-loader/utils/standard-run';
import { QueryCacheService } from 'src/app/services/query-cache.service';
import {
    IQueryContext,
    QueryContextSettingID,
} from 'src/app/services/query-context/models/IQueryContext';
import { Factory } from 'src/app/utils/Factory';
import {
    ICreatedInstanceId,
    IGcpProjectId,
    IQueryCacheService,
} from 'src/app/utils/IFactory';
import {
    ICompiledSQLTokens,
    generateTableFullPath,
    mergeSqlTokensArray,
} from 'src/app/utils/datasource-sql-map/datasource';
import { bq2echartsformat } from 'src/app/utils/echarts/echarts';
import { XYChartAxis } from 'src/app/utils/echarts/xy-chart-axis';
import { GcpProjectId } from 'src/app/utils/gcp-project-id.model';
import { SqlComposer } from 'src/app/utils/query/sql-composer';
import { BiVizRealtimePresenceDatasource } from '../sql-maps/biviz-realtime-presence-sql-map';
import { BiVizRealtimeStatsDatasource } from '../sql-maps/biviz-realtime-stats-sql-map';

export interface RealtimeStatsMainDataLoaderFactoryOptions
    extends ICreatedInstanceId, IGcpProjectId, IQueryCacheService {
    createdInstanceId: string;
    gcpProjectId: GcpProjectId;
    queryCacheService: QueryCacheService;
}

/** This class generates DataLoader instance for analytics vs store reports differences */
export class RealtimeStatsMainDataLoaderFactory extends Factory<
    DataLoader,
    RealtimeStatsMainDataLoaderFactoryOptions
> {
    override FACTORY_CLASS: string = 'RealtimeStatsMainDataLoaderFactory';

    getInstance(options: RealtimeStatsMainDataLoaderFactoryOptions) {
        return new DataLoader(options.createdInstanceId, async function (
            queryContext,
            cancellationToken
        ) {
            // cloone queryContext to avoid modifying the original object
            queryContext = structuredClone(queryContext);

            // remove SALES_INAPP_SOURCE and REALTIME_PURCHASE_ERROR from queryContext
            // this DataLoader is not supporting SALES_INAPP_SOURCE and REALTIME_PURCHASE_ERROR because no metric depends on them
            queryContext.datasourceColumns =
                queryContext.datasourceColumns.filter(
                    (col) =>
                        col.id !== 'SALES_INAPP_SOURCE' &&
                        col.id !== 'REALTIME_PURCHASE_ERROR'
                );

            const process = async () => {
                this.emitter.emit(
                    DataLoaderResult.getLoading(this.latestData, queryContext)
                );

                const biVizRealtimeStats = BiVizRealtimeStatsDatasource;

                // identify columns not supported in datasource
                const biVizRealtimeStatsDsColumns =
                    DataLoader.separatePresentAndMissingDims(
                        queryContext.datasourceColumns,
                        biVizRealtimeStats.sqlMap.columns
                    );

                let latestHourSqlToken:
                    | Partial<ICompiledSQLTokens>
                    | undefined = await (async () => {
                    // if settings.realtime_hours_up_to_latest is true, then we should filter hours to be up to the latest hour
                    if (queryContext.settings.realtimeHoursUpToLatest) {
                        const latest_hour = await DataLoader.runQuery(
                            `SELECT EXTRACT(HOUR FROM MAX(time_hour)) AS latest_hour FROM \`${generateTableFullPath(
                                options.gcpProjectId,
                                biVizRealtimeStats.sqlMap.dataset,
                                biVizRealtimeStats.sqlMap.tableId
                            )}\``,
                            options.queryCacheService
                        );

                        return {
                            where: [
                                BiVizRealtimeStatsDatasource.compileWhereSQLHoursUpToLatest(
                                    latest_hour.result[0].latest_hour
                                ),
                            ],
                        };
                    }
                    return;
                })();

                let currentWeekdaySqlToken:
                    | Partial<ICompiledSQLTokens>
                    | undefined = await (async () => {
                    // if settings.realtime_current_weekday is true, then we should filter weekday to be the current weekday
                    if (queryContext.settings.realtimeCurrentWeekday) {
                        const current_weekday = await DataLoader.runQuery(
                            `SELECT FORMAT_DATETIME("%A", MAX(time_hour)) AS current_weekday FROM \`${generateTableFullPath(
                                options.gcpProjectId,
                                biVizRealtimeStats.sqlMap.dataset,
                                biVizRealtimeStats.sqlMap.tableId
                            )}\``,
                            options.queryCacheService
                        );
                        queryContext.settings.realtimeCurrentWeekday =
                            current_weekday.result[0].current_weekday;

                        return {
                            where: [
                                BiVizRealtimeStatsDatasource.compileWhereSQLCurrentWeekday(
                                    current_weekday.result[0].current_weekday
                                ),
                            ],
                        };
                    }
                    return;
                })();

                const customSQLTokens: Partial<ICompiledSQLTokens> =
                    mergeSqlTokensArray(
                        [latestHourSqlToken, currentWeekdaySqlToken]
                            // filter out undefined values
                            .filter((x) => x) as Partial<ICompiledSQLTokens>[]
                    );

                const biVizRealtimeStatsDsColumnsStatus =
                    DataLoader.getMissingColumnsStatus(
                        biVizRealtimeStatsDsColumns.missing
                    );

                const statsCompiledSQL =
                    biVizRealtimeStats.compileSQLStatements(
                        biVizRealtimeStatsDsColumns.present,
                        queryContext.settings,
                        options,
                        customSQLTokens
                    );

                const biVizRealtimePresence = BiVizRealtimePresenceDatasource;

                const biVizRealtimePresenceDsColumns =
                    DataLoader.separatePresentAndMissingDims(
                        queryContext.datasourceColumns,
                        biVizRealtimePresence.sqlMap.columns
                    );

                const biVizRealtimePresenceDsColumnsStatus =
                    DataLoader.getMissingColumnsStatus(
                        biVizRealtimePresenceDsColumns.missing
                    );

                const presenceCompiledSQL =
                    biVizRealtimePresence.compileSQLStatements(
                        biVizRealtimePresenceDsColumns.present,
                        queryContext.settings,
                        options,
                        customSQLTokens
                    );

                const getMetricSQL = (
                    metricSQLSource: string,
                    metricSQL: string,
                    normalizer: string,
                    normalizedMode: IQueryContext['settings'][QueryContextSettingID.REALTIME_NORMALIZE_METRICS]
                ): string => {
                    switch (normalizedMode) {
                        case 'none':
                            return metricSQL;
                        case 'dau':
                            return `SAFE_DIVIDE(${metricSQLSource}.${metricSQL}, ${normalizer}) AS ${metricSQL}`;
                        default:
                            throw new Error(
                                `Unknown normalization mode: ${normalizedMode}`
                            );
                    }
                };

                const mainMetrics = [
                    'AdRewardGranted_count',
                    'EnterGameContent_count',
                    'ItemCollected_count',
                    'ContentDownloadCompleted_Success_count',
                    'FinishGameContent_count',
                    'GameProgress_25_users_count',
                    'GameProgress_50_users_count',
                    'GameProgress_75_users_count',
                    'GameProgress_25_49_users_count',
                    'GameProgress_50_74_users_count',
                    'GameProgress_75_99_users_count',
                    'ResourceAmountChanged_Energy_in',
                    'ResourceAmountChanged_Energy_out',
                    'ResourceAmountChanged_Gems_in',
                    'ResourceAmountChanged_Gems_out',
                    'ResourceAmountChanged_Hints_in',
                    'ResourceAmountChanged_Hints_out',
                    'ResourceAmountChanged_Coins_in',
                    'ResourceAmountChanged_Coins_out',
                    'ResourceAmountChanged_Compass_in',
                    'ResourceAmountChanged_Compass_out',
                    'DecorationAmountChanged_OwnedAmount_sum',
                    'DecorationAmountChanged_MapAmount_sum',
                    'UnlockSegmentFlowClosed_Success_count',
                    'CloudSaveLoginStarted_count',
                    'CloudSaveLoginCompleted_Success_count',
                    'CloudSaveConflictDetected_count',
                    'CloudSaveConflictResolved_Success_count',
                    'CloudSaveCommitStarted_count',
                    'CloudSaveCommitCompleted_Success_count',
                    'CloudSaveDoubleSessionDetected_count',
                    'CloudSaveRetryCloudOperation_count',
                    'CloudSaveIncompleteSaveDetected_count',
                    'CloudSaveIncompleteSaveResolved_count',
                    'CloudSavePurgeIncompleteStorage_sum',
                    'DialogOpened_IncompleteSaveDialog_count',
                ];

                const statsQuery = `
                SELECT 
                  ${statsCompiledSQL.datetimeTrunc()} AS datetime,
                  ${statsCompiledSQL.splitSelect}
                  ${statsCompiledSQL.cohortSplitSelect}
                  ${mainMetrics
                      .map((metric) => {
                          return `NULLIF( SUM(${metric}), 0) AS ${metric}`;
                      })
                      .join(',\n')}
                FROM \`${generateTableFullPath(
                    options.gcpProjectId,
                    biVizRealtimeStats.sqlMap.dataset,
                    biVizRealtimeStats.sqlMap.tableId
                )}\` ds
                ${statsCompiledSQL.where}
                GROUP BY datetime${statsCompiledSQL.splitGroupBy}${
                    statsCompiledSQL.cohortSplitGroupBy
                }
                `;

                const presenceQuery = `
                SELECT 
                  ${presenceCompiledSQL.datetimeTrunc()} AS datetime,
                  ${presenceCompiledSQL.splitSelect}
                  NULLIF( SUM( installers_count), 0) AS installers_count
                FROM \`${generateTableFullPath(
                    options.gcpProjectId,
                    biVizRealtimePresence.sqlMap.dataset,
                    biVizRealtimePresence.sqlMap.tableId
                )}\` ds
                ${presenceCompiledSQL.where}
                GROUP BY datetime${presenceCompiledSQL.splitGroupBy}
                `;

                const query = `
-- DataLoaderId: ${this.instanceId}
WITH stats AS
(
    ${statsQuery}
)

, presence AS
(
    ${presenceQuery}
)

SELECT
  stats.datetime
  ${statsCompiledSQL.splitSelect ? ', stats.split_group' : ''}
  , ${mainMetrics
      .map((metric) => {
          return `${getMetricSQL(
              'stats',
              `${metric}`,
              'presence.installers_count',
              queryContext.settings[
                  QueryContextSettingID.REALTIME_NORMALIZE_METRICS
              ] ?? 'none'
          )}`;
      })
      .join(',\n')}
  , presence.installers_count
FROM stats
LEFT JOIN presence
ON stats.datetime = presence.datetime
${
    presenceCompiledSQL.splitGroupBy
        ? `-- presence does not contains any non-cohort columns, so we join on split_group
        AND stats.cohort_split_group = presence.split_group`
        : ''
}
${SqlComposer.ORDER_BY([
    `stats.datetime`,
    statsCompiledSQL.splitGroup,
])}                
`;

                const queryResponse = await DataLoader.runQuery(
                    query,
                    options.queryCacheService
                );
                cancellationToken.throwIfCancellationRequested();

                let data = [...queryResponse.result];
                this.latestData = data;
                bq2echartsformat(data);

                XYChartAxis.fillMissingTimeGapRows(
                    data,
                    queryContext,
                    'datetime',
                    queryContext.settings[
                        QueryContextSettingID.DATETIME_AGGREGATION
                    ] ?? 'day'
                );

                this.emitter.emit({
                    data: this.latestData,
                    queryContext: queryContext,
                    // status: pendingDataLoaderResultStatus,
                    status: biVizRealtimeStatsDsColumnsStatus.pendingDataLoaderResultStatus,
                    statusMessage:
                        biVizRealtimeStatsDsColumnsStatus.pendingStatusMessage,
                    isLoading: false,
                    sqlAndJsProcessing: query,
                });
            };

            await standardRun(this, queryContext, process);
        });
    }
}
